import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { TenantSummary, UserSummary } from '@forq/contracts';
import { foundationPermissions, foundationTenants, foundationUser } from './fixtures';

export type ShellMode = 'standard' | 'compact';
export type ThemeMode = 'dark-red' | 'high-contrast';
export type PlatformMode = 'demo' | 'supabase';
export type PermissionStatus = 'ready' | 'loading' | 'error';

export interface PlatformInitialState {
  readonly mode: PlatformMode;
  readonly tenants: readonly TenantSummary[];
  readonly activeTenantId: string;
  readonly user: UserSummary;
  readonly permissions: ReadonlySet<string>;
}

export interface PlatformProviderProps extends PropsWithChildren {
  readonly initialState?: PlatformInitialState;
  readonly resolvePermissions?: (tenantId: string) => Promise<ReadonlySet<string>>;
  readonly onActiveTenantChange?: (tenantId: string) => void;
  readonly onSignOut?: () => Promise<void>;
}

export interface PlatformContextValue {
  readonly mode: PlatformMode;
  readonly tenants: readonly TenantSummary[];
  readonly activeTenant: TenantSummary;
  readonly setActiveTenantId: (tenantId: string) => void;
  readonly user: UserSummary;
  readonly permissions: ReadonlySet<string>;
  readonly permissionStatus: PermissionStatus;
  readonly permissionError: string | null;
  readonly shellMode: ShellMode;
  readonly setShellMode: (mode: ShellMode) => void;
  readonly themeMode: ThemeMode;
  readonly setThemeMode: (mode: ThemeMode) => void;
  readonly isFeatureEnabled: (featureId: string) => boolean;
  readonly setFeatureEnabled: (featureId: string, enabled: boolean) => void;
  /** Globaler „Seiten bearbeiten"-Modus (Frontend-Customizer). An = jede anpassbare Seite zeigt ihre
   *  Bearbeiten-Oberfläche. Wird nur vom Admin umgeschaltet; gilt nur im Gerät/der Sitzung. */
  readonly customizing: boolean;
  readonly setCustomizing: (on: boolean) => void;
  readonly canSignOut: boolean;
  readonly signOut: () => Promise<void>;
}

const PlatformContext = createContext<PlatformContextValue | null>(null);
const disabledFeatureStorageKey = 'forq.disabled-features';

const defaultInitialState: PlatformInitialState = {
  mode: 'demo',
  tenants: foundationTenants,
  activeTenantId: foundationTenants[0].id,
  user: foundationUser,
  permissions: foundationPermissions,
};

function loadDisabledFeatures(): Set<string> {
  try {
    const stored = window.localStorage.getItem(disabledFeatureStorageKey);
    return new Set<string>(stored ? (JSON.parse(stored) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'Berechtigungen konnten nicht geladen werden.';
}

export function PlatformProvider({
  children,
  initialState = defaultInitialState,
  resolvePermissions,
  onActiveTenantChange,
  onSignOut,
}: PlatformProviderProps) {
  const initialTenant = initialState.tenants.find(
    (tenant) => tenant.id === initialState.activeTenantId,
  ) ?? initialState.tenants[0];

  if (!initialTenant) {
    throw new Error('PlatformProvider requires at least one tenant.');
  }

  const [activeTenantId, setActiveTenantIdState] = useState(initialTenant.id);
  const [permissions, setPermissions] = useState<ReadonlySet<string>>(initialState.permissions);
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('ready');
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [shellMode, setShellMode] = useState<ShellMode>('standard');
  const [themeMode, setThemeMode] = useState<ThemeMode>('dark-red');
  const [customizing, setCustomizing] = useState(false);
  const [disabledFeatures, setDisabledFeatures] = useState(loadDisabledFeatures);
  const permissionRequest = useRef(0);

  useEffect(() => () => {
    permissionRequest.current += 1;
  }, []);

  const setFeatureEnabled = useCallback((featureId: string, enabled: boolean) => {
    setDisabledFeatures((current) => {
      const next = new Set(current);
      if (enabled) next.delete(featureId);
      else next.add(featureId);
      window.localStorage.setItem(disabledFeatureStorageKey, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const setActiveTenantId = useCallback((tenantId: string) => {
    if (tenantId === activeTenantId) return;
    if (!initialState.tenants.some((tenant) => tenant.id === tenantId)) {
      setPermissionStatus('error');
      setPermissionError('Der ausgewählte Mandant ist diesem Benutzer nicht zugeordnet.');
      return;
    }

    setActiveTenantIdState(tenantId);
    onActiveTenantChange?.(tenantId);

    if (!resolvePermissions) {
      setPermissionStatus('ready');
      setPermissionError(null);
      return;
    }

    const requestId = permissionRequest.current + 1;
    permissionRequest.current = requestId;
    setPermissions(new Set());
    setPermissionStatus('loading');
    setPermissionError(null);

    void resolvePermissions(tenantId)
      .then((nextPermissions) => {
        if (permissionRequest.current !== requestId) return;
        setPermissions(nextPermissions);
        setPermissionStatus('ready');
      })
      .catch((error: unknown) => {
        if (permissionRequest.current !== requestId) return;
        setPermissions(new Set());
        setPermissionStatus('error');
        setPermissionError(errorMessage(error));
      });
  }, [activeTenantId, initialState.tenants, onActiveTenantChange, resolvePermissions]);

  const signOut = useCallback(async () => {
    if (onSignOut) await onSignOut();
  }, [onSignOut]);

  const activeTenant = initialState.tenants.find(
    (tenant) => tenant.id === activeTenantId,
  ) ?? initialTenant;

  const value = useMemo<PlatformContextValue>(() => ({
    mode: initialState.mode,
    tenants: initialState.tenants,
    activeTenant,
    setActiveTenantId,
    user: initialState.user,
    permissions,
    permissionStatus,
    permissionError,
    shellMode,
    setShellMode,
    themeMode,
    setThemeMode,
    isFeatureEnabled: (featureId) => !disabledFeatures.has(featureId),
    setFeatureEnabled,
    customizing,
    setCustomizing,
    canSignOut: Boolean(onSignOut),
    signOut,
  }), [
    activeTenant,
    customizing,
    disabledFeatures,
    initialState.mode,
    initialState.tenants,
    initialState.user,
    onSignOut,
    permissionError,
    permissions,
    permissionStatus,
    setActiveTenantId,
    setFeatureEnabled,
    shellMode,
    signOut,
    themeMode,
  ]);

  return (
    <PlatformContext.Provider value={value}>
      <div data-theme={themeMode} data-shell={shellMode} data-platform-mode={initialState.mode}>
        {children}
      </div>
    </PlatformContext.Provider>
  );
}

export function usePlatform(): PlatformContextValue {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used inside PlatformProvider.');
  }
  return context;
}
